import React, { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import {
    LogOut,
    LayoutGrid,
    MessageSquareQuote,
    Wand2,
    BookText,
    ShoppingBag,
    Inbox,
    Phone,
    Menu,
    X,
} from "lucide-react";
import { useAdminAuth } from "../../contexts/AdminAuthContext";

const NAV = [
    { to: "/admin/dashboard/portfolio",     label: "Portfolio",    Icon: LayoutGrid },
    { to: "/admin/dashboard/testimonials",  label: "Testimonials", Icon: MessageSquareQuote },
    { to: "/admin/dashboard/services",      label: "Services",     Icon: Wand2 },
    { to: "/admin/dashboard/blog",          label: "Blog",         Icon: BookText },
    { to: "/admin/dashboard/packages",      label: "Packages",     Icon: ShoppingBag },
    { to: "/admin/dashboard/leads",         label: "Leads",        Icon: Inbox },
    { to: "/admin/dashboard/contact",       label: "Contact Info", Icon: Phone },
];

export const AdminLayout = () => {
    const { user, logout } = useAdminAuth();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        window.location.replace("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white flex" data-testid="admin-layout">
            {/* Sidebar — desktop */}
            <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#070707] sticky top-0 h-screen">
                <div className="p-6 border-b border-white/5">
                    <Link to="/admin/dashboard" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-black border border-gold/40 flex items-center justify-center">
                            <span className="font-poppins font-extrabold text-gold text-lg leading-none">R</span>
                        </div>
                        <div className="leading-tight">
                            <div className="font-poppins font-semibold text-white text-sm">Raj <span className="text-gold">Graphics</span></div>
                            <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin Panel</div>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 p-4 space-y-1" data-testid="admin-sidebar-nav">
                    {NAV.map(({ to, label, Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-lg font-inter text-sm transition-colors ${
                                    isActive
                                        ? "bg-gold/10 text-gold border border-gold/30"
                                        : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                                }`
                            }
                        >
                            <Icon size={16} />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/5">
                    <div className="px-3 mb-3">
                        <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500">Signed in</div>
                        <div className="font-poppins text-white text-sm mt-1">{user?.username}</div>
                    </div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="admin-logout-btn"
                        className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/40 hover:text-gold text-zinc-300 font-inter text-sm px-4 py-2.5 rounded-lg transition-colors"
                    >
                        <LogOut size={15} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#070707] border-b border-white/5 h-14 flex items-center justify-between px-4">
                <Link to="/admin/dashboard" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-black border border-gold/40 flex items-center justify-center">
                        <span className="font-poppins font-extrabold text-gold text-base leading-none">R</span>
                    </div>
                    <div className="font-poppins font-semibold text-white text-sm">Admin</div>
                </Link>
                <button
                    type="button"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Toggle admin menu"
                    data-testid="admin-mobile-toggle"
                    className="p-2 text-white"
                >
                    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </header>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-30 pt-14 bg-black/90 backdrop-blur-md"
                    data-testid="admin-mobile-drawer"
                >
                    <nav className="p-4 space-y-1">
                        {NAV.map(({ to, label, Icon }) => (
                            <NavLink
                                key={to}
                                to={to}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-lg font-inter text-sm transition-colors ${
                                        isActive
                                            ? "bg-gold/10 text-gold border border-gold/30"
                                            : "text-zinc-300 hover:bg-white/5 border border-transparent"
                                    }`
                                }
                            >
                                <Icon size={16} />
                                {label}
                            </NavLink>
                        ))}
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="mt-4 w-full flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-zinc-300 font-inter text-sm px-4 py-3 rounded-lg"
                        >
                            <LogOut size={15} />
                            Sign out
                        </button>
                    </nav>
                </div>
            )}

            {/* Content */}
            <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-h-screen" data-testid="admin-content">
                <Outlet />
            </main>
        </div>
    );
};

export default AdminLayout;
